import { Button, type ButtonProps } from '@progress/kendo-react-buttons'
import classNames from 'classnames'
import { produce } from 'immer'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSampleSize } from '~/hooks/useSampleSize'
import { type Brand } from '~/hooks/useSurveyQuery'
import { useTamRatio } from '~/hooks/useTamRatio'
import {
  exists,
  type NineSegment,
  nineSegmentAttributeI18nKeyMap,
  nineSegmentIntentionI18nKeyMap,
  NumFormat,
  toGroupIndex,
  toNineNumber,
} from '~/utils'

import {
  dataDisplayTypeAtom,
  groupAtom,
  groupInfosAtom,
  isPendingTabulationAtom,
  tabulationQueryDataAtom,
} from '../atoms'
import { type GroupInfo } from '../utils'

import styles from './BrandNineSegmentButton.css'

export type BrandNineSegmentButtonProps = {
  brand: Brand
  className?: string
  nineSegment: NineSegment
}

export const BrandNineSegmentButton = ({
  brand,
  className,
  nineSegment,
}: BrandNineSegmentButtonProps) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const sampleSize = useSampleSize()
  const tamRatio = useTamRatio()
  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const activeGroup = useAtomValue(groupAtom)
  const [groupInfos, setGroupInfos] = useAtom(groupInfosAtom)
  const setIsPendingTabulation = useSetAtom(isPendingTabulationAtom)

  const equalsTo = React.useCallback(
    (groupInfo: GroupInfo) =>
      groupInfo.brandNumber === brand.number &&
      groupInfo.nineSegment === nineSegment,
    [brand.number, nineSegment],
  )

  const nineNumber = React.useMemo(
    () => toNineNumber(nineSegment),
    [nineSegment],
  )

  const group = React.useMemo(
    () => groupInfos.find(equalsTo)?.group,
    [groupInfos, equalsTo],
  )

  const countText = React.useMemo(() => {
    const segmentCounts =
      tabulationQueryData?.brands9segs.find(({ id }) => id === brand.id)
        ?.result ?? []

    const count =
      segmentCounts.find(({ segmentNumber }) => segmentNumber === nineNumber)
        ?.count ?? NaN

    if (Number.isNaN(count)) {
      return '-'
    }

    const ratio = count / sampleSize

    return NumFormat.forCount(count, ratio, tamRatio, dataDisplayType)
  }, [
    brand.id,
    dataDisplayType,
    nineNumber,
    sampleSize,
    tabulationQueryData?.brands9segs,
    tamRatio,
  ])

  const handleClick = React.useCallback<EH<ButtonProps, 'onClick'>>(() => {
    if (activeGroup !== group) {
      setGroupInfos(
        produce((draft) => {
          const groupInfo = draft.find(equalsTo)
          if (exists(groupInfo)) {
            groupInfo.group = activeGroup
          } else {
            draft.push({
              brandNumber: brand.number,
              nineSegment,
              group: activeGroup,
            })
          }
        }),
      )

      logEvent('Select segment', {
        eventProperties: [
          { key: 'brand', value: brand.name },
          { key: 'segment', value: `${nineNumber}` },
          { key: 'group', value: `${toGroupIndex(activeGroup)}` },
        ],
      })
    } else {
      setGroupInfos(R.filter(R.isNot(equalsTo)))
    }
    setIsPendingTabulation(true)
  }, [
    activeGroup,
    brand.name,
    brand.number,
    equalsTo,
    group,
    logEvent,
    nineNumber,
    nineSegment,
    setGroupInfos,
    setIsPendingTabulation,
  ])

  return (
    <Button
      className={classNames(
        styles.root,
        { [styles[`${group}`]]: exists(group) },
        className,
      )}
      onClick={handleClick}
    >
      <Box backgroundColor="transparent" className={styles.border} />

      <VStack backgroundColor="transparent" spacing={0}>
        <Typography variant="normal">
          <span className={classNames(styles.buttonText, styles.segmentNumber)}>
            {nineNumber}
          </span>
        </Typography>

        <Typography variant="normal">
          <span className={styles.buttonText}>
            {t(nineSegmentIntentionI18nKeyMap[nineSegment])}
          </span>
        </Typography>

        <Typography variant="normal">
          <span className={styles.buttonText}>
            {t(nineSegmentAttributeI18nKeyMap[nineSegment])}
          </span>
        </Typography>
      </VStack>

      <Box backgroundColor="transparent">
        <Typography variant="normal">
          <span className={styles.buttonText}>{countText}</span>
        </Typography>
      </Box>
    </Button>
  )
}
